/**
 * Was mit einer angetippten Anlage geschehen soll (#735).
 *
 * Ein Tipp auf eine Anlage hat bisher sofort die Detailseite geöffnet — auf dem Plan die
 * einzige Reaktion, und für „ich will das Ding nur verschieben“ die falsche. Statt die eine
 * Aktion zu erraten, fragt dieses Menü: verschieben, löschen, oder eben doch die Detailseite.
 *
 * Es liegt frei über der Seite, dort wo getippt wurde, damit Finger und Blick am selben Ort
 * bleiben. Am Rand des Fensters rückt es nach innen, sonst stünde es halb außerhalb.
 *
 * Oben steht die Kurzansicht des Geräts (tri-server#1364): Zustand, Druck, Temperatur,
 * Batterie, letzter Uplink, nächste Prüfung — nur zum Ansehen. Sie lädt für sich und blockiert
 * die Aktionen darunter nicht.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, MapPin, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import AssetQuickView from "../components/AssetQuickView";

interface Properties {
  asset: any;
  /** Tipp-Position in Fensterkoordinaten. */
  x: number;
  y: number;
  onMove: () => void;
  onDelete: () => void;
  onDetails: () => void;
  onClose: () => void;
}

const MENU_WIDTH = 272;
/** Height assumed before the menu has been measured (actions only). */
const MENU_HEIGHT = 148;
const EDGE = 8;

export default function AssetActionMenu(properties: Properties) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement | null>(null);
  // The quick view changes the menu's height as it loads; clamp with the measured height.
  const [menuHeight, setMenuHeight] = useState(MENU_HEIGHT);

  useLayoutEffect(() => {
    const height = menuRef.current?.offsetHeight;
    if (height && height !== menuHeight) setMenuHeight(height);
  });

  useEffect(() => {
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        properties.onClose();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") properties.onClose();
    };

    // Ein Tipp daneben schließt — auf dem Tablet die einzige Geste, die jeder kennt.
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [properties]);

  const left = Math.min(Math.max(EDGE, properties.x), window.innerWidth - MENU_WIDTH - EDGE);
  const top = Math.min(Math.max(EDGE, properties.y), window.innerHeight - menuHeight - EDGE);

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label={t("assetMenu.title")}
      style={{ position: "fixed", left, top, width: MENU_WIDTH, zIndex: 50 }}
      className="flex flex-col gap-0.5 rounded-md border border-border bg-popover p-1 shadow-lg"
    >
      {properties.asset?._id ? (
        <>
          {/* The quick view's header names the device, so there is no separate title row. */}
          <AssetQuickView
            assetId={String(properties.asset._id)}
            title={properties.asset?.name || properties.asset?.id}
          />
          <Separator className="my-0.5" />
        </>
      ) : (
        <div className="truncate px-2 py-1 text-xs text-muted-foreground">
          {properties.asset?.name || properties.asset?.id || t("assetMenu.title")}
        </div>
      )}
      <Button type="button" role="menuitem" variant="ghost" className="h-8 justify-start px-2" onClick={properties.onMove}>
        <MapPin aria-hidden="true" className="mr-2 h-4 w-4" />
        {t("assetMenu.move")}
      </Button>
      <Button type="button" role="menuitem" variant="ghost" className="h-8 justify-start px-2" onClick={properties.onDetails}>
        <ExternalLink aria-hidden="true" className="mr-2 h-4 w-4" />
        {t("assetMenu.details")}
      </Button>
      {/* Destructive action apart from the look and the everyday actions above. */}
      <Separator data-testid="asset-menu-delete-separator" className="my-0.5" />
      <Button
        type="button"
        role="menuitem"
        variant="ghost"
        className="h-8 justify-start px-2 text-destructive hover:text-destructive"
        onClick={properties.onDelete}
      >
        <Trash2 aria-hidden="true" className="mr-2 h-4 w-4" />
        {t("assetMenu.delete")}
      </Button>
    </div>
  );
}
